import { getActiveCarrierId, workforceFetch } from '../workforceBff';

export class MadarApiError extends Error {
  constructor({ error, code, repair = {}, domain, carrier } = {}, { status = 0, requestId = '' } = {}) {
    super(error || 'درخواست مدار انجام نشد. دوباره تلاش کنید.');
    this.name = 'MadarApiError';
    this.error = this.message;
    this.code = code || 'INTERNAL';
    this.repair = repair;
    this.status = status;
    this.requestId = requestId;
    this.domain = domain;
    this.carrier = carrier;
  }
}
export const enc = encodeURIComponent;
export function query(values = {}) {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null) params.set(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
  });
  const encoded = params.toString();
  return encoded ? `?${encoded}` : '';
}
function checkAbort(carrierId, signal) {
  if (signal?.aborted || carrierId !== getActiveCarrierId()) throw new DOMException('Cancelled', 'AbortError');
}
// The existing bridge owns the carrier header, CSRF and in-flight carrier cancellation.
// Successful JSON is returned verbatim, including zero-created/full-skipped results.
export async function request(method, path, body, options = {}) {
  const { signal, idempotencyKey, download = false } = options;
  const carrierId = getActiveCarrierId();
  checkAbort(carrierId, signal);
  try {
    const { supabase } = await import('../supabaseClient');
    const { data } = await supabase.auth.getSession();
    checkAbort(carrierId, signal);
    const multipart = typeof FormData !== 'undefined' && body instanceof FormData;
    const response = await workforceFetch(path, {
      method, signal,
      headers: { Accept: download ? '*/*' : 'application/json',
        ...(data?.session?.access_token ? { Authorization: `Bearer ${data.session.access_token}` } : {}),
        ...(body !== undefined && !multipart ? { 'Content-Type': 'application/json' } : {}),
        ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}) },
      ...(body !== undefined ? { body: multipart ? body : JSON.stringify(body) } : {}),
    });
    checkAbort(carrierId, signal);
    const requestId = response.headers?.get('X-Request-Id') || '';
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      checkAbort(carrierId, signal);
      throw new MadarApiError(payload, { status: response.status, requestId });
    }
    if (download) {
      const blob = await response.blob();
      checkAbort(carrierId, signal);
      return { blob, result_state: response.headers?.get('X-Madar-Result-State'),
        content_type: response.headers?.get('Content-Type'), disposition: response.headers?.get('Content-Disposition') };
    }
    const payload = response.status === 204 ? null : await response.json();
    checkAbort(carrierId, signal);
    return payload;
  } catch (error) {
    if (error.name === 'AbortError' || error instanceof MadarApiError) throw error;
    checkAbort(carrierId, signal);
    throw new MadarApiError({ code: 'NETWORK', error: 'ارتباط با سامانه برقرار نشد. دوباره تلاش کنید.' });
  }
}
// validated and invalid end the current operation: validation waits for a person, invalid execution is final.
const TERMINAL = new Set(['validated', 'invalid', 'executed', 'failed', 'cancelled', 'rejected', 'expired']);
function pause(ms, signal) {
  return new Promise((resolve, reject) => {
    const finish = () => { signal?.removeEventListener('abort', abort); resolve(); };
    const timer = setTimeout(finish, ms);
    const abort = () => { clearTimeout(timer); signal?.removeEventListener('abort', abort); reject(new DOMException('Cancelled', 'AbortError')); };
    if (signal?.aborted) abort();
    else signal?.addEventListener('abort', abort, { once: true });
  });
}
export async function poll(fetchJob, id, options = {}) {
  const carrierId = getActiveCarrierId();
  let delay = 2000;
  for (;;) {
    checkAbort(carrierId, options.signal);
    const result = await fetchJob(id, options);
    checkAbort(carrierId, options.signal);
    options.onUpdate?.(result);
    if (TERMINAL.has(result.job?.status || result.status)) return result;
    await pause(delay, options.signal);
    delay = Math.min(10000, Math.round(delay * 1.5));
  }
}
